import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDown, Pen, Trash2, Eye, Play, Square, RefreshCw, FileText, Calculator } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SortIcon } from '@/components/common/SortIcon';
import type { Contract, Currency } from '../types';
import { getContractStatusBadgeColor, getContractTypeBadgeColor } from '../statusStyles';

const toCurrency = (amount: number, locale: string, currency: Currency): string => {
  if (currency === 'UZS') {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'UZS',
      maximumFractionDigits: 0,
    }).format(amount);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

// Maps a contract's stored `periodType` to the i18n key the contract form uses
// for the same option, so the billing-period column shows the cycle type
// (Weekly / Monthly / Instance / …) instead of the removed `period.interval`.
const PERIOD_TYPE_LABEL_KEYS: Record<NonNullable<Contract['periodType']>, string> = {
  WEEK: 'weekly',
  MONTH: 'monthly',
  QUARTER: 'quarter',
  HALF_YEAR: 'semiAnnual',
  YEAR: 'yearly',
  INSTANCE: 'billingInstance',
  MANUAL: 'manual',
};

interface ContractColumnsProps {
  onEdit?: (contract: Contract) => void;
  onDelete?: (contract: Contract) => void;
  onActivate?: (contract: Contract) => void;
  onClose?: (contract: Contract) => void;
  onRenew?: (contract: Contract) => void;
  onStartBilling?: (contract: Contract) => void;
  onStopBilling?: (contract: Contract) => void;
  onRecalculate?: (contract: Contract) => void;
  hasAccess?: (resource: string, action: string) => boolean;
  orderBy?: string;
  order?: 'asc' | 'desc';
  onSort?: (column: string) => void;
}

export function useContractColumns({
  onEdit,
  onDelete,
  onActivate,
  onClose,
  onRenew,
  onStartBilling,
  onStopBilling,
  onRecalculate,
  hasAccess = () => true,
  orderBy,
  order,
  onSort,
}: ContractColumnsProps = {}): ColumnDef<Contract>[] {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns: ColumnDef<Contract>[] = [
    {
      accessorKey: 'name',
      header: () => (
        <div
          className="flex cursor-pointer items-center"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('name')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('name')}
        >
          <p>{t('nameThing')}</p>
          <SortIcon active={orderBy === 'name'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const contract = row.original;
        return (
          <Link
            to={`/app/contracts/${contract.id}`}
            className="text-blue-700 hover:underline"
          >
            {contract.name || `#${contract.id}`}
          </Link>
        );
      },
    },
    {
      accessorKey: 'clientName',
      header: () => <p>{t('clientName')}</p>,
      cell: ({ row }) => {
        const client = row.original.client;
        if (!client) return '-';
        return (
          <p>
            {client.lastname || ''} {client.name || ''} {client.middlename || ''}
          </p>
        );
      },
    },
    {
      accessorKey: 'clientPhone',
      header: () => <p>{t('clientPhone')}</p>,
      cell: ({ row }) => {
        const client = row.original.client;
        return <p>{client?.phone || '-'}</p>;
      },
    },
    {
      accessorKey: 'clientType',
      header: () => <p>{t('clientType')}</p>,
      cell: ({ row }) => {
        const client = row.original.client;
        return <p>{client?.type || '-'}</p>;
      },
    },
    {
      accessorKey: 'status',
      header: () => (
        <div
          className="flex cursor-pointer items-center"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('status')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('status')}
        >
          <p>{t('status.status')}</p>
          <SortIcon active={orderBy === 'status'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge className={`rounded-full text-white ${getContractStatusBadgeColor(status)}`}>
            {t(`status.${status}`)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'type',
      header: () => (
        <div
          className="flex cursor-pointer items-center"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('type')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('type')}
        >
          <p>{t('type')}</p>
          <SortIcon active={orderBy === 'type'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const type = row.original.type;
        return (
          <Badge className={`rounded-full text-white ${getContractTypeBadgeColor(type)}`}>
            {t(`status.${type}`)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'balance',
      header: () => (
        <div className="flex items-center">
          <p>{t('balance')}</p>
        </div>
      ),
      cell: ({ row }) => {
        const contract = row.original;
        const balance = contract.balance ?? 0;
        const currency = contract.currency ?? 'USD';
        const color = balance < 0 ? 'bg-red-500' : 'bg-gray-300';
        return (
          <Badge className={`rounded-full text-white ${color}`}>
            {toCurrency(balance, 'uz-UZ', currency)}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'advancePaymentUnit',
      header: () => <p>{t('advancePayment')}</p>,
      cell: ({ row }) => {
        const { advancePayment, advancePaymentUnit, currency, price } = row.original;
        if (!advancePayment) return '-';
        if (advancePaymentUnit === 'percent') {
          return `${((advancePayment * (price ?? 0)) / 100).toFixed(1)}=${advancePayment}%`;
        }
        return currency === 'USD'
          ? `$ ${advancePayment?.toLocaleString() || ''}`
          : `UZS ${advancePayment?.toLocaleString() || ''}`;
      },
    },
    {
      accessorKey: 'billingPeriod',
      header: () => <p>{t('billingPeriod')}</p>,
      cell: ({ row }) => {
        const { type, periodType, billing_period_usage_percent } = row.original;
        const periodText =
          type === 'HOURLY'
            ? periodType
              ? t(PERIOD_TYPE_LABEL_KEYS[periodType])
              : '-'
            : t('fullPeriod');
        const percent = billing_period_usage_percent;
        return (
          <>
            {periodText}{' '}
            <Badge>{percent == null ? '-' : `${Number(percent).toFixed(0)} %`}</Badge>
          </>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: () => (
        <div
          className="flex cursor-pointer select-none items-center"
          role="button"
          tabIndex={0}
          onClick={() => onSort?.('createdAt')}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSort?.('createdAt')}
        >
          <p>{t('createdAt')}</p>
          <SortIcon active={orderBy === 'createdAt'} direction={order} />
        </div>
      ),
      cell: ({ row }) => {
        const date = row.original.createdAt;
        return date ? format(new Date(date), 'yyyy-MM-dd HH:mm') : '-';
      },
    },
  ];

  // Add actions column if user has permissions
  if (hasAccess('contract', 'update') || hasAccess('contract', 'delete')) {
    columns.splice(0, 0, {
      id: 'actions',
      header: t('actionsLabel'),
      cell: ({ row }) => {
        const contract = row.original;
        const canEdit = hasAccess('contract', 'update');
        const canDelete = hasAccess('contract', 'delete');
        const canCreateBill = hasAccess('bill', 'create');
        const canStartBilling = hasAccess('billing-period', 'start');
        const canStopBilling = hasAccess('billing-period', 'stop');

        return (
          <div className="flex">
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onEdit?.(contract)}
                className="rounded-r-none"
              >
                <Pen size={16} />
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={canEdit ? 'rounded-l-none border-l-0' : ''}
                >
                  <ChevronDown size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="flex min-w-[150px] flex-col gap-1">
                <DropdownMenuItem onClick={() => navigate(`/app/contracts/${contract.id}`)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t('view')}
                </DropdownMenuItem>

                {/* Status actions based on current status */}
                {contract.status === 'NEW' && canEdit && (
                  <DropdownMenuItem onClick={() => onActivate?.(contract)}>
                    <Play className="mr-2 h-4 w-4" />
                    {t('activate')}
                  </DropdownMenuItem>
                )}

                {contract.status !== 'NEW' && canEdit && (
                  <DropdownMenuItem onClick={() => onRenew?.(contract)}>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    {t('setAsNew')}
                  </DropdownMenuItem>
                )}

                {contract.status === 'ACTIVE' && canEdit && (
                  <DropdownMenuItem onClick={() => onClose?.(contract)}>
                    <Square className="mr-2 h-4 w-4" />
                    {t('close')}
                  </DropdownMenuItem>
                )}

                {/* Billing period actions for INSTANCE-period contracts */}
                {contract.periodType === 'INSTANCE' && contract.status === 'ACTIVE' && (
                  <>
                    {contract.billingPeriods && contract.billingPeriods.length > 0 ? (
                      canStopBilling && onStopBilling && (
                        <DropdownMenuItem onClick={() => onStopBilling(contract)}>
                          <Square className="mr-2 h-4 w-4" />
                          {t('stopBillingPeriod')}
                        </DropdownMenuItem>
                      )
                    ) : (
                      canStartBilling && onStartBilling && (
                        <DropdownMenuItem onClick={() => onStartBilling(contract)}>
                          <Play className="mr-2 h-4 w-4" />
                          {t('startBillingPeriod')}
                        </DropdownMenuItem>
                      )
                    )}
                  </>
                )}

                {/* Re-price every task from scratch. Listed on every contract, but
                    only HOURLY contracts have per-task prices to replay — the rest
                    show it disabled rather than failing on click. */}
                {canEdit && onRecalculate && (
                  <DropdownMenuItem
                    onClick={() => onRecalculate(contract)}
                    disabled={contract.type !== 'HOURLY'}
                    title={
                      contract.type !== 'HOURLY' ? t('recalculateTasksHourlyOnly') : undefined
                    }
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    {t('recalculateTasks')}
                  </DropdownMenuItem>
                )}

                {/* Download contract */}
                {contract.type !== 'PRO_BONO' && (
                  <DropdownMenuItem asChild>
                    <a
                      href={`${import.meta.env.VITE_URL}/contracts/${contract.id}.docx`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      {contract.name}
                    </a>
                  </DropdownMenuItem>
                )}

                {canDelete && contract.status !== 'ACTIVE' && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => onDelete?.(contract)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {t('delete')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    });
  }

  return columns;
}
